import { Box, Skeleton, useToast } from '@chakra-ui/react';
import { usePaginationAdmin } from '@diamond/shared/context';
import { BannerCrud } from '@diamond/shared/schema/banner';
import { PersonalizedBannerItem } from '@diamond/shared/types';
import { Divider, DropResult, Pagination, showToast } from '@diamond/shared/ui';
import { LIST_FEATURE_FLAGS, useFeatureFlag } from '@diamond/shared/utils';
import { AdminLayout } from '@diamond/sol-admin/components';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useCleanSearchParams } from '@diamond/sol-admin/hooks';
import { useQueryClient } from '@tanstack/react-query';

import { reorderBanner } from '../api';
import { BannerHeader } from '../components/banner-header';
import { BannerList } from '../components/banner-list';
import { BannerModalManager } from '../components/banner-modal-manager';
import {
  BACKOFFICE_BANNER_LEGACY_QUERY_KEY,
  BACKOFFICE_BANNER_QUERY_KEY,
  BANNER_PAGE_SIZE,
} from '../constant';
import { useDeleteBannerMutation } from '../hooks';
import { useBannerListLegacy, usePersonalizedBanners } from '../hooks/list';

export function BannerPage() {
  const session = useAuthStore();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { paramsEntries, setParams } = useCleanSearchParams();
  const currentPage = !isNaN(Number(paramsEntries['page']))
    ? Number(paramsEntries['page'])
    : 1;

  const links = [
    {
      title: 'Beranda',
      link: '/',
    },
    {
      title: 'Banner Management',
      link: '/banners',
    },
  ];

  const isNewCg3 = useFeatureFlag(LIST_FEATURE_FLAGS.FF_BANNER_CG3_NEW, false);

  const {
    data: bannerResponse,
    isLoading: isLoadingCg3,
    refetch: refetchCg3,
  } = usePersonalizedBanners(
    { page: currentPage, size: BANNER_PAGE_SIZE },
    { enabled: isNewCg3 }
  );

  const {
    data: legacyBanners,
    isLoading: isLoadingLegacy,
    refetch: refetchLegacy,
  } = useBannerListLegacy({ enabled: !isNewCg3 });

  const data: PersonalizedBannerItem[] = isNewCg3
    ? (
        bannerResponse as
          | BannerCrud['getManyBannerPersonalization']['response']
          | undefined
      )?.data || []
    : // Map legacy array directly; types are compatible on used fields
      (legacyBanners as PersonalizedBannerItem[]) || [];

  const responseWithPagination = bannerResponse as
    | BannerCrud['getManyBannerPersonalization']['response']
    | undefined;
  const pageCount = isNewCg3
    ? responseWithPagination?.pagination?.pages || 1
    : 1;
  const refetchActive = isNewCg3 ? refetchCg3 : refetchLegacy;
  const { nextPage, previousPage, lastPage, firstPage } = usePaginationAdmin(
    currentPage,
    (value) => setParams({ page: `${value}` }),
    refetchActive,
    pageCount
  );

  const deleteMutation = useDeleteBannerMutation({
    onSuccess: () => {
      showToast(toast, 'success', 'Banner berhasil dihapus');
    },
    onSettled: () => {
      if (isNewCg3) {
        refetchCg3();
        queryClient.invalidateQueries({
          queryKey: [BACKOFFICE_BANNER_LEGACY_QUERY_KEY],
        });
      } else {
        refetchLegacy();
        queryClient.invalidateQueries({
          queryKey: [BACKOFFICE_BANNER_QUERY_KEY],
        });
      }
    },
  });

  const isLoading = isNewCg3 ? isLoadingCg3 : isLoadingLegacy;
  if (isLoading) {
    return (
      <AdminLayout>
        <Box py={4}>
          <Skeleton height="540px" borderRadius="md" />
        </Box>
      </AdminLayout>
    );
  }

  const handleIndexChange = (result: DropResult) => {
    if (result.destination) {
      reorderBanner(
        session.access_token,
        result.draggableId,
        result.destination.index + 1
      ).then(() => {
        // Option C: Refetch active, invalidate inactive
        if (isNewCg3) {
          refetchCg3();
          queryClient.invalidateQueries({
            queryKey: [BACKOFFICE_BANNER_LEGACY_QUERY_KEY],
          });
        } else {
          refetchLegacy();
          queryClient.invalidateQueries({
            queryKey: [BACKOFFICE_BANNER_QUERY_KEY],
          });
        }
      });
    }
  };

  const handleMoveToTop = (id: string) => {
    reorderBanner(session.access_token, id, 1)
      .then(() => {
        if (isNewCg3) {
          refetchCg3();
          queryClient.invalidateQueries({
            queryKey: [BACKOFFICE_BANNER_LEGACY_QUERY_KEY],
          });
        } else {
          refetchLegacy();
          queryClient.invalidateQueries({
            queryKey: [BACKOFFICE_BANNER_QUERY_KEY],
          });
        }
        showToast(toast, 'success', 'Banner berhasil dipindahkan ke atas');
      })
      .catch((error) => {
        showToast(toast, 'error', `Gagal memindahkan banner: ${error.message}`);
      });
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  return (
    <AdminLayout>
      <BannerModalManager
        data={data as PersonalizedBannerItem[]}
        isNewCg3={isNewCg3}
        onSuccess={() => {
          if (isNewCg3) {
            refetchCg3();
            queryClient.invalidateQueries({
              queryKey: [BACKOFFICE_BANNER_LEGACY_QUERY_KEY],
            });
          } else {
            refetchLegacy();
            queryClient.invalidateQueries({
              queryKey: [BACKOFFICE_BANNER_QUERY_KEY],
            });
          }
        }}
      >
        {({ onAdd, onEdit, onDuplicate }) => (
          <>
            <BannerHeader links={links} onAdd={onAdd} />

            <Divider />

            <BannerList
              data={data as PersonalizedBannerItem[]}
              onDelete={handleDelete}
              onEdit={onEdit}
              onDuplicate={onDuplicate}
              onIndexChange={handleIndexChange}
              onMoveToTop={handleMoveToTop}
              totalItems={bannerResponse?.pagination.rows}
              itemsPerPage={10}
            />
          </>
        )}
      </BannerModalManager>

      {pageCount > 1 && (
        <Box mt={8} display="flex" justifyContent="center">
          <Pagination
            currentPage={currentPage}
            totalPage={pageCount}
            nextPage={nextPage}
            previousPage={previousPage}
            firstPage={firstPage}
            lastPage={lastPage}
          />
        </Box>
      )}
    </AdminLayout>
  );
}
